/**
 * Audio feature (order `FEATURE_ORDER.audio`; audio-direction §2–§7, D-044): the synthesized SFX palette through
 * the audio engine, ship sounds, the lock tone, ship-computer alarms, comms, and the adaptive score.
 *
 * - Boot: the SFX bank pre-renders progressively in idle time (OfflineAudioContext needs no gesture), priority
 *   sounds first.
 * - `bus 'firstGesture'` ("Click to fly"): the AudioContext, the engine and comms are created and resumed; the
 *   score (Tone.js) loads lazily as its own chunk and starts when ready. Later gestures resume the context
 *   (`bus 'resume'`), `bus 'pause'` suspends it (the score's transport stops with it).
 * - Each frame: settings → engine; the listener follows the floating-origin camera; combat events play through
 *   the router; the player's drive, boost and RCS; the rising lock tone; alarms; at 4 Hz the music feed
 *   (nearest raider, engagement) and on events its stingers (kill, wave cleared, death, respawn); the engine's
 *   own update (ducking, pan).
 * - DEV: F7 speaks a sample comms line. Test builds: `window.__audioDebug` (tests/e2e/audio.spec.ts).
 *
 * `frame` allocates nothing but the one `AudioBufferSourceNode` per sound started (accepted, see engine.ts).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { lockProgress } from '../../combat/lock'
import { isHostile } from '../../combat/hostility'
import { logger } from '../../core/logger'
import { createAlarmState, stepAlarms, type AlarmState } from '../../audio/alarms'
import { commsConfig } from '../../audio/comms/commsSchema'
import { createComms, type CommsSystem } from '../../audio/comms'
import { createAudioEngine, LOOP, ROUTE, type AudioEngine } from '../../audio/engine'
import { beepRate, createLockToneState, stepLockTone, type LockToneState } from '../../audio/lockTone'
import type { MusicOptions, MusicSystem } from '../../audio/music'
import { desiredLevel } from '../../audio/music/intensity'
import { musicConfig } from '../../audio/music/musicSchema'
import { createMusicFeed, stepMusicFeed, type MusicFeedState } from '../../audio/musicFeed'
import { createAudioRouter, createCombatSummary, resetCombatSummary, routeEvents, skipEvents, type AudioRouter, type CombatSummary } from '../../audio/router'
import { browserIdleScheduler, createSfxBank, startPrerender, type SfxBank } from '../../audio/sfx/bank'
import { browserOfflineFactory, renderRaw, variantSeed, type OfflineFactory } from '../../audio/sfx/render'
import { sfxConfig, soundIndex, SOUND_IDS, type SfxConfig, type UiSoundId } from '../../audio/sfxSchema'
import { createShipSoundState, stepShipSounds, type ShipSoundState, type ShipThrust } from '../../audio/shipSounds'
import { PRIORITY } from '../../audio/voices'
import { PLAYER_TEAM } from '../../sim'
import { Hull, Life, Lock, Pos64, Ship, Team, Thrust } from '../../world/components'

/** What the feature needs from an `AudioContext` (a real one, or a fake in tests). */
export type FeatureAudioContext = BaseAudioContext & {
  resume(): Promise<void>
  suspend(): Promise<void>
  close(): Promise<void>
}

export type AudioContextFactory = () => FeatureAudioContext

/** Creates the score (the lazy music chunk in the game; a fake in tests). */
export type MusicLoader = (ac: BaseAudioContext, out: AudioNode, opts: MusicOptions) => Promise<MusicSystem>

export interface AudioFeatureDeps {
  createContext: AudioContextFactory
  /** Starts filling the bank (default: progressive offline pre-render in idle time). */
  prerender: (bank: SfxBank, config: SfxConfig) => void
  /** Offline renders for comms lines and the score's assets (null: comms and music stay off). */
  offline: OfflineFactory | null
  loadMusic: MusicLoader
}

/** Main-thread cost of `frame` (ms): last, smoothed and the worst since the last reset. */
export interface AudioFrameStats {
  last: number
  avg: number
  max: number
  frames: number
}

export interface AudioFeature extends GameFeature {
  readonly config: SfxConfig
  readonly bank: SfxBank
  /** Null until the first gesture (or when Web Audio is unavailable). */
  readonly engine: AudioEngine | null
  readonly context: FeatureAudioContext | null
  readonly comms: CommsSystem | null
  /** Null until the score's chunk has loaded and started. */
  readonly music: MusicSystem | null
  readonly frameStats: AudioFrameStats
  /** Plays a UI sound (e.g. `uiClick` from the menu). No-op before the first gesture. */
  playUi(id: UiSoundId): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

function defaultContextFactory(): FeatureAudioContext {
  return new AudioContext({ latencyHint: 'interactive' })
}

function defaultPrerender(bank: SfxBank, config: SfxConfig): void {
  const factory = browserOfflineFactory()
  if (factory === null) {
    logger.warn('audio: OfflineAudioContext unavailable; sound effects stay silent')
    return
  }
  startPrerender(bank, config, {
    factory,
    schedule: browserIdleScheduler(),
    createBuffer: (channels, length, sampleRate) => new AudioBuffer({ numberOfChannels: channels, length, sampleRate }),
  })
}

const defaultLoadMusic: MusicLoader = async (ac, out, opts) => {
  // Tone.js prints a banner on import unless this is set before the chunk evaluates.
  ;(globalThis as { TONE_SILENCE_LOGGING?: boolean }).TONE_SILENCE_LOGGING = true
  const m = await import('../../audio/music')
  return m.createMusic(ac, out, opts)
}

function logRejection(what: string): (err: unknown) => void {
  return (err: unknown): void => logger.warn(`audio: ${what} failed`, err)
}
const onResumeFailed = logRejection('resume')
const onSuspendFailed = logRejection('suspend')
const onCloseFailed = logRejection('close')
const onCommsFailed = logRejection('comms line')

/** Test-build hook (tests/e2e/audio.spec.ts). */
export interface AudioDebug {
  state(): string
  sfxReady(): number
  voices(): number
  frameStats(): AudioFrameStats
  music(): { level: number; playing: number; bars: number; braaams: number } | null
  /**
   * Largest sample difference between fresh offline renders of two variations of one SFX (determinism in the
   * real browser: Chromium's SIMD paths differ in the last float bit, so equal seeds agree to about −130 dB).
   */
  renderDiff(id: string, a: number, b: number): Promise<number>
  say(seed: number, stress: number): Promise<number>
}

declare global {
  interface Window {
    __audioDebug?: AudioDebug
  }
}

/** DEV: the sample characters F7 speaks as (the same seeds as the audition page). */
const DEV_CHARACTERS = [101, 202, 307] as const

export function createAudioFeature(config: SfxConfig = sfxConfig, deps: Partial<AudioFeatureDeps> = {}): AudioFeature {
  const createContext = deps.createContext ?? defaultContextFactory
  const prerender = deps.prerender ?? defaultPrerender
  const offline = deps.offline === undefined ? browserOfflineFactory() : deps.offline
  const loadMusic = deps.loadMusic ?? defaultLoadMusic
  const bank = createSfxBank(config)
  let game: GameContext | null = null
  let ac: FeatureAudioContext | null = null
  let engine: AudioEngine | null = null
  let comms: CommsSystem | null = null
  let music: MusicSystem | null = null
  let router: AudioRouter | null = null
  let paused = false
  let failed = false
  let prerendered = false
  let waveWasActive = false
  let disposed = false
  const summary: CombatSummary = createCombatSummary()
  const lock: LockToneState = createLockToneState()
  const ship: ShipSoundState = createShipSoundState()
  const alarms: AlarmState = createAlarmState()
  const feed: MusicFeedState = createMusicFeed()
  const thrust: ShipThrust = { surge: 0, sway: 0, heave: 0, pitch: 0, yaw: 0, roll: 0 }
  const frameStats: AudioFrameStats = { last: 0, avg: 0, max: 0, frames: 0 }
  const lockBeep = soundIndex('lockBeep')
  const rcsPuff = soundIndex('rcsPuff')
  const unsubscribe: (() => void)[] = []

  function startMusic(): void {
    const e = engine
    if (e === null || offline === null) return
    loadMusic(e.ac, e.musicIn, { factory: offline })
      .then((m) => {
        if (disposed || engine !== e) {
          m.dispose()
          return
        }
        music = m
        m.start()
        logger.info(`audio: score ready (${m.key.region}, ${m.key.modeName}, ${m.key.bpm} BPM)`)
      })
      .catch((err: unknown) => logger.warn('audio: the score failed to load; playing without music', err))
  }

  function startAudio(): void {
    if (failed || game === null) return
    if (ac !== null) {
      if (!paused) ac.resume().catch(onResumeFailed)
      return
    }
    try {
      ac = createContext()
      engine = createAudioEngine(ac, bank, config)
      engine.applySettings(game.settings)
      if (offline !== null) comms = createComms(engine, commsConfig, offline, config.render.sampleRate)
      logger.info(`audio: engine up at ${ac.sampleRate} Hz; ${bank.ready}/${bank.ids.length} sounds rendered so far`)
    } catch (err: unknown) {
      failed = true
      engine = null
      comms = null
      logger.warn('audio: Web Audio unavailable; the game runs silent', err)
      return
    }
    if (router !== null) skipEvents(router, game.sim.events)
    ac.resume().catch(onResumeFailed)
    startMusic()
  }

  function say(seed: number, stress: number): void {
    if (comms !== null) comms.say(seed, stress).catch(onCommsFailed)
  }

  function onDevKey(e: KeyboardEvent): void {
    if (e.code !== 'F7' || e.repeat) return
    e.preventDefault()
    const k = Math.floor(Math.random() * DEV_CHARACTERS.length)
    say(DEV_CHARACTERS[k], Math.random())
  }

  /** Distance (m) from (x, y, z) to the nearest living ship hostile to the player (`isHostile`); Infinity when none. Allocation-free. */
  function nearestHostile(ctx: GameContext, x: number, y: number, z: number): number {
    const ships = ctx.sim.ships.dense
    let best = Infinity
    for (let i = 0; i < ships.length; i++) {
      const eid = ships[i]
      if (!isHostile(PLAYER_TEAM, Team.id[eid]) || Life.alive[eid] !== 1) continue
      const dx = Pos64.x[eid] - x
      const dy = Pos64.y[eid] - y
      const dz = Pos64.z[eid] - z
      const d = dx * dx + dy * dy + dz * dz
      if (d < best) best = d
    }
    return Math.sqrt(best)
  }

  function installDebugHook(): void {
    if (import.meta.env.MODE !== 'test' || typeof window === 'undefined') return
    window.__audioDebug = {
      state: () => (ac === null ? 'none' : ac.state),
      sfxReady: () => bank.ready,
      voices: () => (engine === null ? 0 : engine.activeVoices()),
      frameStats: () => ({ ...frameStats }),
      music: () => (music === null ? null : { level: music.intensity.level, playing: music.playing, bars: music.stats.bars, braaams: music.stats.braaams }),
      async renderDiff(id: string, a: number, b: number): Promise<number> {
        const i = soundIndex(id)
        const f = offline
        if (f === null || i < 0) throw new Error(`cannot render ${id}`)
        const def = config.sounds[SOUND_IDS[i]]
        const seed = config.render.seed
        const [x, y] = await Promise.all([renderRaw(f, def, config.render.sampleRate, variantSeed(seed, i, a)), renderRaw(f, def, config.render.sampleRate, variantSeed(seed, i, b))])
        let max = 0
        for (let c = 0; c < x.numberOfChannels; c++) {
          const p = x.getChannelData(c)
          const q = y.getChannelData(c)
          for (let k = 0; k < p.length; k++) max = Math.max(max, Math.abs(p[k] - q[k]))
        }
        return max
      },
      async say(seed: number, stress: number): Promise<number> {
        if (comms === null) throw new Error('comms not running')
        return (await comms.say(seed, stress)).utterance.seconds
      },
    }
  }

  const feature: AudioFeature = {
    name: 'audio',
    order: FEATURE_ORDER.audio,
    config,
    bank,
    frameStats,
    get engine(): AudioEngine | null {
      return engine
    },
    get context(): FeatureAudioContext | null {
      return ac
    },
    get comms(): CommsSystem | null {
      return comms
    },
    get music(): MusicSystem | null {
      return music
    },

    init(ctx: GameContext): void {
      game = ctx
      disposed = false
      // Throws at boot when a weapon's sfx key is missing from the bank.
      router = createAudioRouter(ctx.sim.data.weapons.weapons, config)
      skipEvents(router, ctx.sim.events)
      if (!prerendered) {
        prerendered = true
        prerender(bank, config)
      }
      unsubscribe.push(
        ctx.bus.on('firstGesture', () => {
          paused = false
          startAudio()
        }),
        ctx.bus.on('resume', () => {
          paused = false
          // Resume only: the context is created by the first gesture.
          if (ac !== null) startAudio()
        }),
        ctx.bus.on('pause', () => {
          paused = true
          if (ac !== null) ac.suspend().catch(onSuspendFailed)
        }),
      )
      if (import.meta.env.DEV && typeof window !== 'undefined') {
        window.addEventListener('keydown', onDevKey)
        unsubscribe.push(() => window.removeEventListener('keydown', onDevKey))
      }
      installDebugHook()
    },

    playUi(id: UiSoundId): void {
      if (engine !== null) engine.playUi(id)
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const r = router
      if (r === null) return
      const sim = ctx.sim
      const e = engine
      if (e === null) {
        skipEvents(r, sim.events)
        return
      }
      const t0 = performance.now()
      // Applied while paused too, so the menu's sliders are already right on resume (only changes are sent).
      e.applySettings(ctx.settings)
      if (f.paused) {
        skipEvents(r, sim.events)
        return
      }
      const now = e.ac.currentTime
      const l = e.listener
      l.x = ctx.rig.pos.x
      l.y = ctx.rig.pos.y
      l.z = ctx.rig.pos.z
      l.qx = ctx.rig.rot.x
      l.qy = ctx.rig.rot.y
      l.qz = ctx.rig.rot.z
      l.qw = ctx.rig.rot.w

      const p = sim.playerEid
      const alive = p >= 0 && Life.alive[p] === 1
      resetCombatSummary(summary)
      routeEvents(r, sim.events, alive ? p : -1, e, summary)

      if (p >= 0) {
        thrust.surge = Thrust.surge[p]
        thrust.sway = Thrust.sway[p]
        thrust.heave = Thrust.heave[p]
        thrust.pitch = Thrust.pitch[p]
        thrust.yaw = Thrust.yaw[p]
        thrust.roll = Thrust.roll[p]
      }
      stepShipSounds(ship, config.ship, thrust, alive && Ship.boosting[p] === 1, alive, now, rcsPuff, e)

      const locked = alive && Lock.locked[p] === 1
      const progress = alive && Lock.held[p] > 0 ? lockProgress(p, sim.data.weapons) : 0
      const gain = config.lockTone.gain
      if (stepLockTone(lock, progress, locked, f.dt, config.lockTone)) e.play(lockBeep, gain, ROUTE.ui, PRIORITY.alarm, 0, beepRate(progress, config.lockTone))
      e.setLoop(LOOP.lock, lock.solid, gain, 1, 20000)

      const waveActive = sim.waves.active
      const waveCleared = waveWasActive && !waveActive
      waveWasActive = waveActive
      const hull = alive ? Hull.value[p] / sim.defs[Ship.defIndex[p]].hull : 0
      stepAlarms(alarms, config.alarms, hull, alive, summary, waveCleared, now, e)

      const m = music
      if (m !== null) {
        if (summary.playerDied) m.stinger('death')
        if (summary.playerRespawned) m.stinger('recover')
        if (summary.playerKills > 0) m.stinger('kill')
        if (waveCleared) m.stinger('waveCleared')
        const near = alive ? nearestHostile(ctx, Pos64.x[p], Pos64.y[p], Pos64.z[p]) : Infinity
        const elapsed = stepMusicFeed(feed, f.dt, summary, near, musicConfig.intensity.feedHz)
        if (elapsed > 0) m.feed(desiredLevel(feed.input, musicConfig.intensity), elapsed)
      }

      e.update(now)
      const ms = performance.now() - t0
      frameStats.last = ms
      frameStats.avg += (ms - frameStats.avg) * 0.05
      if (ms > frameStats.max) frameStats.max = ms
      frameStats.frames++
    },

    dispose(): void {
      disposed = true
      for (let i = 0; i < unsubscribe.length; i++) unsubscribe[i]()
      unsubscribe.length = 0
      if (music !== null) music.dispose()
      if (comms !== null) comms.dispose()
      if (engine !== null) engine.dispose()
      if (ac !== null) ac.close().catch(onCloseFailed)
      music = null
      comms = null
      engine = null
      ac = null
      game = null
      router = null
      failed = false
    },
  }
  return feature
}

const feature: AudioFeature = createAudioFeature()

/** Plays a UI sound through the game's audio feature (the menu's clicks). */
export function playUiSound(id: UiSoundId): void {
  feature.playUi(id)
}

export default feature
